
import styles from "./RepresentanteLegal.module.scss";
import React,{useEffect,useState} from "react";
import {Controller,useForm} from "react-hook-form";
import {useLocation,useNavigate} from "react-router-dom";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {Input} from "shared/components/inputs/Input";
import {PriceInput} from "shared/components/inputs/PriceInput";
import {buscarEnderecos} from "shared/services/api/endereco.api";
import {IEnderecoUser} from "shared/interfaces/IEnderecoUser";

const schema=z.object({
  mother_name:z.string().min(2,"Nome da mãe é obrigatório"),
  monthly_income:z.number({
    message:"Ganhos mensais são obrigatórios",
  }).positive("Ganhos mensais devem ser maiores que 0"),
  professional_occupation:z.string().min(2,"Ocupação profissional é obrigatória"),
  self_declared_legal_representative:z.boolean().refine(
    (value)=>value===true,
    {message:"É necessário confirmar que você é o representante legal da empresa"}
  ),
  addressId:z.string().uuid("Selecione um endereço do representante legal"),
});

export type RepresentanteLegalFormData=z.infer<typeof schema>;

interface RepresentanteLegalProps{
  onNext?:(data:RepresentanteLegalFormData)=>void;
  defaultValues?:Partial<RepresentanteLegalFormData>;
  modoEdicao?:boolean;
  lojaId?:string;
  onSalvo?:(dados:RepresentanteLegalFormData)=>void;
  dadosInformacoes?:unknown;
  dadosEndereco?:unknown;
}

interface CadastroLojaNavigationState{
  origem?:string;
  returnTo?:string;
  enderecoId?:string;
  dadosInformacoes?:unknown;
  dadosRepresentante?:Partial<RepresentanteLegalFormData>|null;
  dadosEndereco?:unknown;
  abaRetorno?:string;
}

export const RepresentanteLegal:React.FC<RepresentanteLegalProps>=({
  onNext,
  defaultValues,
  modoEdicao,
  lojaId,
  onSalvo,
  dadosInformacoes,
  dadosEndereco,
})=>{
  const navigate=useNavigate();
  const location=useLocation();
  const [enderecos,setEnderecos]=useState<IEnderecoUser[]>([]);
  const [carregandoEnderecos,setCarregandoEnderecos]=useState(true);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState:{errors},
  }=useForm<RepresentanteLegalFormData>({
    resolver:zodResolver(schema),
    defaultValues:{
      mother_name:defaultValues?.mother_name??"",
      monthly_income:defaultValues?.monthly_income??0,
      professional_occupation:defaultValues?.professional_occupation??"",
      self_declared_legal_representative:defaultValues?.self_declared_legal_representative??false,
      addressId:defaultValues?.addressId??"",
    },
  });

  const addressId=watch("addressId");

  useEffect(()=>{
    const carregarEnderecos=async()=>{
      try{
        setCarregandoEnderecos(true);
        const lista=await buscarEnderecos();
        setEnderecos(lista);
      }catch(error){
        console.error("Erro ao carregar endereços:",error);
      }finally{
        setCarregandoEnderecos(false);
      }
    };

    carregarEnderecos();
  },[]);

  useEffect(()=>{
    const state=location.state as CadastroLojaNavigationState|null;
    if(!state||state.origem!=="cadastro-loja")return;

    if(state.enderecoId){
      setValue("addressId",state.enderecoId,{
        shouldValidate:true,
        shouldDirty:true,
      });
    }

    if(state.dadosRepresentante){
      const dados=state.dadosRepresentante;

      if(dados.mother_name!==undefined){
        setValue("mother_name",dados.mother_name);
      }

      if(dados.monthly_income!==undefined){
        setValue("monthly_income",dados.monthly_income);
      }

      if(dados.professional_occupation!==undefined){
        setValue("professional_occupation",dados.professional_occupation);
      }

      if(dados.self_declared_legal_representative!==undefined){
        setValue("self_declared_legal_representative",dados.self_declared_legal_representative);
      }

      if(dados.addressId!==undefined&&!state.enderecoId){
        setValue("addressId",dados.addressId);
      }
    }
  },[location.state,setValue]);

  const abrirMeusEnderecos=()=>{
    const valoresAtuais:RepresentanteLegalFormData={
      mother_name:watch("mother_name"),
      monthly_income:watch("monthly_income"),
      professional_occupation:watch("professional_occupation"),
      self_declared_legal_representative:watch("self_declared_legal_representative"),
      addressId:watch("addressId"),
    };

    if(modoEdicao&&lojaId){
      navigate("/meusenderecos",{
        state:{
          selecionarEndereco:true,
          returnTo:`/editarloja/${lojaId}`,
          origem:"editar-loja",
          abaRetorno:"representante",
          dadosRepresentante:valoresAtuais,
        },
      });
      return;
    }

    navigate("/meusenderecos",{
      state:{
        selecionarEndereco:true,
        returnTo:"/cadastroloja",
        origem:"cadastro-loja",
        abaRetorno:"representante",
        dadosInformacoes:location.state?.dadosInformacoes??dadosInformacoes??null,
        dadosRepresentante:valoresAtuais,
        dadosEndereco:location.state?.dadosEndereco??dadosEndereco??null,
      },
    });
  };

  const onSubmit=(data:RepresentanteLegalFormData)=>{
    if(modoEdicao&&lojaId){
      onSalvo?.(data);
      return;
    }

    onNext?.(data);
  };

  return(
    <form id="representante-legal-form" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Representante Legal</h2>
          <p>Informe apenas os dados do representante legal da empresa para a validação da loja. O único dado de outra pessoa é o Nome da Mãe do representante.</p>
        </div>

        <div className={styles.fields}>
          <Input
            name="mother_name"
            label="Nome da Mãe do Representante Legal"
            placeholder="Digite o nome completo da mãe"
            register={register}
            error={errors.mother_name?.message}
            required
          />

          <Controller
            name="monthly_income"
            control={control}
            render={({field})=>(
              <div>
                <label htmlFor="monthly_income">Ganhos Mensais do Representante Legal</label>
                <PriceInput
                  value={(field.value??0)*100}
                  onChange={(centavos)=>field.onChange(centavos/100)}
                  placeholder="0,00"
                  error={errors.monthly_income?.message}
                />
              </div>
            )}
          />

          <Input
            name="professional_occupation"
            label="Ocupação Profissional do Representante Legal"
            placeholder="Digite sua ocupação profissional"
            register={register}
            error={errors.professional_occupation?.message}
            required
          />
        </div>

        <div className={styles.addressSection}>
          <div className={styles.sectionHeader}>
            <h3>Endereço do Representante Legal</h3>
            <p>Selecione seu endereço residencial para vincular ao representante legal.</p>
          </div>

          {carregandoEnderecos?(
            <p>Carregando endereços...</p>
          ):(
            <>
              {enderecos.length>0&&(
                <div className={styles.fields}>
                  <div>
                    <label htmlFor="addressId">Endereço</label>
                    <select
                      id="addressId"
                      {...register("addressId")}
                      value={addressId??""}
                      onChange={(event)=>{
                        setValue("addressId",event.target.value,{
                          shouldValidate:true,
                          shouldDirty:true,
                        });
                      }}
                    >
                      <option value="">Selecione um endereço</option>

                      {enderecos.map((endereco)=>(
                        <option key={endereco.id} value={endereco.id}>
                          {endereco.nome} - {endereco.rua}, {endereco.numero} -{" "}
                          {endereco.cidade}/{endereco.estado}
                        </option>
                      ))}
                    </select>

                    {errors.addressId&&(
                      <span className={styles.error}>{errors.addressId.message}</span>
                    )}
                  </div>
                </div>
              )}

              {enderecos.length===0&&<p>Nenhum endereço cadastrado.</p>}

              <button type="button" onClick={abrirMeusEnderecos}>
                {enderecos.length>0?"Selecionar ou cadastrar endereço":"Cadastrar endereço"}
              </button>
            </>
          )}
        </div>

        <div className={styles.declaration}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              {...register("self_declared_legal_representative")}
            />

            <span>
              Declaro que sou o representante legal da empresa e assumo a responsabilidade
              pelas informações fornecidas neste cadastro, estando diretamente associado à
              loja.
            </span>
          </label>

          {errors.self_declared_legal_representative&&(
            <span className={styles.error}>
              {errors.self_declared_legal_representative.message}
            </span>
          )}
        </div>
      </div>
    </form>
  );
};
